import React from "react";
import {
  Modal,
  Card,
  Space,
  Tag,
  Typography,
  Row,
  Col,
  Avatar,
  Divider,
  Button,
} from "antd";
import {
  EyeOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CloseOutlined,
  CheckOutlined,
  UserOutlined,
} from "@ant-design/icons";

const PERMISSION_ICONS = {
  View: <EyeOutlined />,
  Create: <PlusOutlined />,
  Edit: <EditOutlined />,
  Delete: <DeleteOutlined />,
  Reviewer: null,
  Authoriser: null,
};

const { Title, Text } = Typography;

/**
 * Role Permissions Modal
 *
 * Props:
 * - open
 * - role
 * - onClose
 * - onDelete
 * - onAssignUsers
 * - onEditPermissions
 */
const RolePermissionsModal = ({
  open,
  role,
  onClose
}) => {
  const PermissionTag = ({ label, enabled }) => (
    <Tag
      icon={enabled ? <CheckOutlined /> : <CloseOutlined />}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        padding: "6px 14px",
        fontSize: 13,
        borderRadius: 20,
        border: "none",
        fontWeight: 500,
        background: enabled
          ? "linear-gradient(135deg, #22c55e, #16a34a)"
          : "#e5e7eb",
        color: enabled ? "#fff" : "#6b7280",
      }}
    >
      {PERMISSION_ICONS[label]} {label}
    </Tag>
  );

  return (
    <Modal
      open={open}
      onCancel={onClose}
      closable={false}
      footer={
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            type="primary"
            size="large"
            onClick={onClose}
            style={{
              minWidth: 110,
              borderRadius: 999,
              fontWeight: 600,
              background: "#003a7a",
              borderColor: "#003a7a",
            }}
          >
            Done
          </Button>
        </div>
      }
      width={900}
      centered
      className="tab_modal"
      destroyOnClose
      bodyStyle={{ padding: 0, borderRadius: 18, overflow: "hidden" }}
    >
      {role && (
        <>
          {/* ===== Header ===== */}
          <div
            style={{
              padding: "20px 24px 14px",
              background: "linear-gradient(135deg, #f8fafc, #eef2f7)",
              borderBottom: "1px solid #e5e7eb",
            }}
          >
            <Space direction="vertical" size={6}>
              <Title level={3} style={{ margin: 0, fontWeight: 700 }}>
                {role.name}
              </Title>

              <Space wrap>
                <Tag
                  style={{
                    borderRadius: 999,
                    padding: "2px 10px",
                    fontWeight: 600,
                  }}
                  color="blue"
                >
                  Role
                </Tag>

                <Tag
                  color={
                    role.assigned_users_count > 0 ? "gold" : "default"
                  }
                  icon={<UserOutlined />}
                  style={{
                    borderRadius: 999,
                    padding: "2px 10px",
                    fontWeight: 600,
                  }}
                >
                  {role.assigned_users_count} user
                  {role.assigned_users_count !== 1 && "s"}
                </Tag>

                {role.assigned_users_count > 0 && (
                  <Tag color="gold">In Use</Tag>
                )}
              </Space>

              <Text type="secondary">
                {role.description || "Role permission configuration"}
              </Text>
            </Space>
          </div>

          {/* ===== Body ===== */}
          <div
            style={{
              padding: 20,
              background:
                "linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%)",
              maxHeight: "70vh",
              overflowY: "auto",
            }}
          >
            <Row gutter={[20, 20]}>
              {role.module_permissions
                ?.filter(
                  (perm) =>
                    perm.can_view ||
                    perm.can_create ||
                    perm.can_edit ||
                    perm.can_delete ||
                    perm.can_maker ||
                    perm.can_checker
                )
                  .map((perm) => {
                    const moduleSlug = typeof perm.module === "string" ? perm.module : perm.module?.slug;
                    const moduleName = typeof perm.module === "string" 
                      ? perm.module.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()) 
                      : perm.module?.name;
                    const firstChar = moduleName ? moduleName.charAt(0) : "";

                    return (
                      <Col span={24} md={12} key={perm.id}>
                        <Card
                          hoverable
                          bordered={false}
                          style={{
                            borderRadius: 18,
                            height: "100%",
                            border: "1px solid #e2e8f0",
                            boxShadow: "0 10px 24px rgba(2, 8, 23, 0.08)",
                            transition: "all 0.25s ease",
                          }}
                          bodyStyle={{ padding: 18 }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.transform = "translateY(-4px)")
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.transform = "translateY(0)")
                          }
                        >
                          {/* Module Header */}
                          <Space align="start" size={14}>
                            <Avatar
                              size={40}
                              style={{
                                background:
                                  "linear-gradient(135deg, #2563eb, #3b82f6)",
                                fontWeight: 600,
                              }}
                            >
                              {firstChar}
                            </Avatar>

                            <div>
                              <Title level={5} style={{ margin: 0 }}>
                                {moduleName}
                              </Title>
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                Module permissions
                              </Text>
                            </div>
                          </Space>

                          <Divider style={{ margin: "14px 0" }} />

                          <Space wrap size={[10, 10]}>
                            <PermissionTag
                              label="View"
                              enabled={perm.can_view}
                            />
                            <PermissionTag
                              label="Create"
                              enabled={perm.can_create}
                            />
                            <PermissionTag
                              label="Edit"
                              enabled={perm.can_edit}
                            />
                            <PermissionTag
                              label="Delete"
                              enabled={perm.can_delete}
                            />
                            {moduleSlug === "kyc_kyb" && (
                              <>
                                <PermissionTag
                                  label="Reviewer"
                                  enabled={perm.can_maker}
                                />
                                <PermissionTag
                                  label="Authoriser"
                                  enabled={perm.can_checker}
                                />
                              </>
                            )}
                          </Space>
                        </Card>
                      </Col>
                    );
                  })}
            </Row>
          </div>
        </>
      )}
    </Modal>
  );
};

export default RolePermissionsModal;
